/**
 * ResetUsageModal - Admin Portal Component
 * 
 * Modal for resetting promo code usage count and redemptions.
 * Requires admin authorization and reason for audit trail.
 */

import React, { useState } from 'react';
import './ResetUsageModal.css';

const LAMBDA_URL = 'https://fytckhfpoh5wgzodartzjtse640ueugs.lambda-url.ap-southeast-1.on.aws';

const ResetUsageModal = ({ isOpen, onClose, code, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleReset = async () => {
    if (!reason.trim() || reason.trim().length < 10) {
      setError('Reason must be at least 10 characters');
      return;
    }

    if (!confirmed) {
      setError('Please confirm the reset action');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get ID token from Cognito
      const { getIdToken } = await import('aws-amplify/auth');
      const { idToken } = (await getIdToken()).tokens;

      const response = await fetch(`${LAMBDA_URL}/reset`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          code: code.code,
          reason: reason.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset usage');
      }

      const data = await response.json();
      
      // Success
      if (onSuccess) {
        onSuccess(data.code);
      }
      
      handleClose();
    } catch (err) {
      console.error('Reset usage error:', err);
      setError(err.message || 'Failed to reset usage');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setConfirmed(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Reset Usage</h3>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-warning">
            <div className="modal-warning__icon">⚠️</div>
            <div className="modal-warning__text">
              <strong>Warning:</strong> This action will reset the usage count and clear all redemption history for this promo code.
            </div>
          </div>

          <div className="modal-code-info">
            <div className="modal-code-info__label">Promo Code:</div>
            <div className="modal-code-info__value">{code.code}</div>
          </div>

          <div className="modal-usage-stats">
            <div className="modal-usage-stats__item">
              <span className="modal-usage-stats__label">Current Usage:</span>
              <span className="modal-usage-stats__value">{code.usageCount} / {code.maxUsage}</span>
            </div>
            <div className="modal-usage-stats__item">
              <span className="modal-usage-stats__label">Redemptions:</span>
              <span className="modal-usage-stats__value">{code.redemptions?.length || 0}</span>
            </div>
          </div>

          <div className="modal-form-group">
            <label className="modal-label" htmlFor="reset-reason">
              Reason for Reset <span className="modal-required">*</span>
            </label>
            <textarea
              id="reset-reason"
              className="modal-textarea"
              placeholder="Enter reason for resetting usage (minimum 10 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              disabled={loading}
            />
            <div className="modal-char-count">
              {reason.length} / 10 minimum
            </div>
          </div>

          <div className="modal-checkbox-group">
            <label className="modal-checkbox">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
              />
              <span>I understand this will clear all usage data and redemption history</span>
            </label>
          </div>

          {error && (
            <div className="modal-error">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="modal-button modal-button--secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="modal-button modal-button--danger"
            onClick={handleReset}
            disabled={loading || !reason.trim() || reason.trim().length < 10 || !confirmed}
          >
            {loading ? 'Resetting...' : 'Reset Usage'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetUsageModal;
